import { createAction } from '@reduxjs/toolkit'
import Axios from 'axios'
import { DialogOptionActionManifestJson } from '../API/DialogJson'
import { WaypointType } from '../API/InteractableObjectJson'
import { LevelManifestJson } from '../API/LevelJson'
import { validateLevel } from '../API/validation'
import Config from '../Containers/Config'
import InventoryContainer from '../Containers/InventoryContainer/InventoryContainer'
import LabelContainer from '../Containers/LabelContainer/LabelContainer'
import SubtitlesContainer from '../Containers/SubtitlesContainer/SubtitlesContainer'
import AudioController from '../Controllers/AudioController'
import InventoryCombinationsController from '../Controllers/InventoryCombinationsController'
import dispatch from '../Utility/dispatchAction'
import Dialog from './Dialog/Dialog'
import { createDialogOptionActionListener } from './Dialog/DialogOption'
import GameScene from './GameScene/GameScene'
import Waypoint from './Waypoint'

export const levelActionCreator = createAction('Level', (level: Level, action: LevelActionsAction) => ({
    payload: {
        level,
        action,
    },
}))

export enum LevelActionsAction {
    start = 'start',
    stop = 'stop',
}

export type LevelProps = {
    id: string
    name: string
    config: {
        next: {
            cinematic?: string
            level?: string
        }
        firstGameScene?: string
    }
    soundtrackAudioId?: string
    introAudioId?: string
    // TODO needed here?
    startingItemIds: Array<string>
    gameScenes: Array<GameScene>
    dialogs: Array<Dialog>
}

export default class Level {
    id: string
    config: {
        next: {
            cinematic?: string
            level?: string
        }
        firstGameScene?: string
    }
    soundtrackAudioId?: string
    introAudioId?: string
    introPlayed = false
    startingItemIds: Array<string>

    gameScenes: Record<string, GameScene>
    dialogs: Record<string, Dialog>

    activeGameSceneId?: string
    activeDialogId?: string

    constructor(manifest: LevelProps) {
        this.id = manifest.id
        this.config = manifest.config
        this.soundtrackAudioId = manifest.soundtrackAudioId
        this.introAudioId = manifest.introAudioId
        this.startingItemIds = manifest.startingItemIds

        this.gameScenes = manifest.gameScenes.reduce(
            (acc, gameScene) => ({
                ...acc,
                [gameScene.id]: gameScene,
            }),
            {}
        )

        this.dialogs = manifest.dialogs.reduce(
            (acc, dialog) => ({
                ...acc,
                [dialog.id]: dialog,
            }),
            {}
        )

        this.activeGameSceneId = manifest.config.firstGameScene

        addEventListener('Waypoint.interact', async (e) => {
            // @ts-ignore -- WHY: can't use CustomEvent here somehow
            const waypoint = e.detail.waypoint as Waypoint

            switch (waypoint.type) {
                case WaypointType.gameScene:
                    // WHY: waypoint id is currently the id of where it's pointing
                    await this.changeGameScene(waypoint.id)
                    break
                case WaypointType.level:
                    dispatch(levelActionCreator(this, LevelActionsAction.stop))
            }
        })

        createDialogOptionActionListener((option) => {
            if (option.action?.activate) {
                this.activateDialogOptions(option.action.activate)
            }
        })
    }

    static load = async (levelId: string) => {
        // load manifest
        const manifest = await Level.loadLevelManifest(levelId)
        console.debug(`Inamove::Level => manifest loaded`)

        const introAudioId = manifest.intro && AudioController.add(manifest.intro)
        const soundtrackAudioId = manifest.soundtrack && AudioController.add(manifest.soundtrack)

        // load gameScenes
        const gameScenes = await Promise.all(
            manifest.gameScenes.map(async (gameSceneId) => await GameScene.load(gameSceneId))
        )
        console.debug(`Inamove::Level => GameScenes loaded`)

        const dialogs = await Promise.all(manifest.dialogs.map(async (dialogId) => await Dialog.load(dialogId)))
        console.debug(`Inamove::Level => Dialogs loaded`)

        // takeableObjects.addItems
        // recursive get combinations
        const takeableItemIds: Array<string> = gameScenes.reduce(
            (acc, gameScene) => [
                ...acc,
                ...Object.keys(gameScene.takeableObjects).map(
                    (takeableObjectId) => gameScene.takeableObjects[takeableObjectId].inventoryItemId
                ),
            ],
            [] as Array<string>
        )

        InventoryCombinationsController.loadCombinationsForLevel(manifest.startingItems, gameScenes)
        const combinedItemIds = Object.keys(InventoryCombinationsController.loadedCombinations).flatMap(
            (id) => InventoryCombinationsController.loadedCombinations[id].addItems
        )

        InventoryContainer.loadItems([...manifest.startingItems, ...takeableItemIds, ...combinedItemIds])
        console.debug(`Inamove::Level => Items loaded`)

        const props: LevelProps = {
            id: manifest.id,
            name: manifest.name,
            introAudioId,
            soundtrackAudioId,
            config: manifest.config,
            startingItemIds: manifest.startingItems,
            gameScenes,
            dialogs,
        }

        return new Level(props)
    }

    static loadLevelManifest = async (levelId: string) => {
        const baseUrl = `${Config.LEVEL_PATH}${levelId}/`
        const getAudioUrl = (src: string) => `${baseUrl}${src}${Config.AUDIO_FILE_EXT}`
        const getVideoUrl = (src: string) => `${Config.ASSET_PATH}video/${src}${Config.VIDEO_FILE_EXT}`

        const response = await Axios.get(`${baseUrl}${levelId}.json`, {
            responseType: 'json',
        })

        const manifest: LevelManifestJson = response.data

        if (!validateLevel) {
            throw new Error('Inamove::Level => could not get validation!')
        }

        if (!validateLevel(manifest)) {
            console.error(`Inamove::Level => LevelManifest validation for "${levelId}" failed:`, validateLevel.errors)
            throw new Error()
        }

        const withCorrectUrls: LevelManifestJson = {
            ...manifest,
            intro: manifest.intro && getAudioUrl(manifest.intro),
            soundtrack: manifest.soundtrack && getAudioUrl(manifest.soundtrack),
            config: {
                ...manifest.config,
                next: {
                    ...manifest.config.next,
                    cinematic: manifest.config.next.cinematic && getVideoUrl(manifest.config.next.cinematic),
                },
            },
        }

        return withCorrectUrls
    }

    startDialog = async (dialogId: string) => {
        InventoryContainer.hide()
        const dialog = this.dialogs[dialogId]
        if (dialog) {
            this.activeDialogId = dialogId
            if (dialog.soundtrackAudioId) {
                AudioController.stop(this.soundtrackAudioId)
            }
            await dialog.start()
            InventoryContainer.show()
        }
    }

    stopDialog = () => {
        this.activeDialogId && this.dialogs[this.activeDialogId].stop()
    }

    startGame = (gameSceneID?: string) => {
        this.activeGameSceneId = gameSceneID ?? this.activeGameSceneId

        this.soundtrackAudioId && AudioController.loop(this.soundtrackAudioId)

        if (!this.introPlayed && this.introAudioId) {
            AudioController.play(this.introAudioId)
            this.introPlayed = true
        }

        InventoryContainer.addItems(this.startingItemIds)
        InventoryContainer.show()

        this.activeGameSceneId && this.gameScenes[this.activeGameSceneId].start()
    }

    stopGame = (stopSoundtrack = false) => {
        // TODO: Why?
        /*
        Object.keys(this.gameScenes).forEach((gameSceneId) => {
            this.gameScenes[gameSceneId].stop()
        })
        */
        this.activeGameSceneId && this.gameScenes[this.activeGameSceneId].stop()
        stopSoundtrack && AudioController.stop(this.soundtrackAudioId)

        InventoryContainer.hide()
        LabelContainer.clear()
        SubtitlesContainer.clear()
    }

    stop = () => {
        this.stopGame(true)
        this.stopDialog()
    }

    start = () => {
        this.startGame(this.activeGameSceneId)
    }

    destroy = () => {
        this.stop()

        Object.keys(this.gameScenes).forEach((gameSceneId) => {
            this.gameScenes[gameSceneId].destroy()
        })
        Object.keys(this.dialogs).forEach((dialogId) => {
            this.dialogs[dialogId].destroy()
        })

        this.introAudioId && AudioController.remove(this.introAudioId)
        this.soundtrackAudioId && AudioController.remove(this.soundtrackAudioId)

        this.gameScenes = {}
        this.dialogs = {}
    }

    changeGameScene = async (gameSceneId: string) => {
        if (this.activeGameSceneId) {
            await this.gameScenes[this.activeGameSceneId].stop()
        }
        this.activeGameSceneId = gameSceneId
        await this.gameScenes[gameSceneId].start()
    }

    activateDialogOptions = (activate: Required<DialogOptionActionManifestJson['activate']>) => {
        if (activate && Object.keys(this.dialogs).includes(activate.dialog)) {
            const dialog = this.dialogs[activate.dialog]

            dialog.parts[activate.part].options[activate.option].available = true
            dialog.parts[activate.part].options[activate.option].show()
        }
    }
}
